import { Alert } from "@/deps/ui-library";
import { useCashFlow, useFiscalYears } from "@/api/queries";
import type { CashFlowLine, CashFlowReport } from "@/api/types";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { PeriodField, StatementTable, useDateParam, type StatementRow } from "./reportParts";
import { ReportFrame } from "./ReportFrame";

const paisa = (value: string) => Math.round(Number(value) * 100);
const rupees = (value: number) => (value / 100).toFixed(2);
const sum = (lines: CashFlowLine[]) => lines.reduce((total, line) => total + paisa(line.amount), 0);

export function cashFlowRows(flow: CashFlowReport): { rows: StatementRow[]; difference: number } {
  const section = (
    title: string,
    totalLabel: string,
    lines: CashFlowLine[],
    extra: StatementRow[] = [],
    extraPaisa = 0,
  ) => [
    { label: title, kind: "header" as const, values: [null] },
    ...extra,
    ...lines.map((line) => ({ label: line.name, kind: "line" as const, values: [line.amount] })),
    { label: totalLabel, kind: "subtotal" as const, values: [rupees(sum(lines) + extraPaisa)] },
  ];
  const net =
    paisa(flow.netResult) + sum(flow.operating) + sum(flow.investing) + sum(flow.financing);
  return {
    rows: [
      ...section(
        "Operating activities",
        "Net cash from operations",
        flow.operating,
        [{ label: "Net result", kind: "line", values: [flow.netResult] }],
        paisa(flow.netResult),
      ),
      ...section("Investing activities", "Net cash from investing", flow.investing),
      ...section("Financing activities", "Net cash from financing", flow.financing),
      { label: "Net change in cash", kind: "total", values: [rupees(net)] },
      { label: `Cash at ${formatDate(flow.from)}`, kind: "line", values: [flow.openingCash] },
      { label: `Cash at ${formatDate(flow.to)}`, kind: "total", values: [flow.closingCash] },
    ],
    difference: net - (paisa(flow.closingCash) - paisa(flow.openingCash)),
  };
}

/** The indirect method, for a range that starts in a fiscal year. */
export function CashFlowPage() {
  const now = today();
  const { data: years } = useFiscalYears();
  const year =
    years?.find((y) => y.start <= now && now <= y.end) ?? years?.find((y) => y.isCurrent);
  const [from, setFrom] = useDateParam("/reports/cash-flow", "from", year?.start ?? now);
  const [to, setTo] = useDateParam("/reports/cash-flow", "to", now);

  const { data, isPending, error } = useCashFlow(from, to, years !== undefined);
  const built = data ? cashFlowRows(data) : undefined;
  const period = `${formatDate(from)} – ${formatDate(to)}`;

  return (
    <ReportFrame
      id="cf"
      title="Cash flow"
      period={period}
      exportable={
        built
          ? () => ({
              title: "Cash flow",
              period,
              columns: [{ header: "" }, { header: "Amount", numeric: true }],
              rows: built.rows.map((row) => [row.label, ...row.values]),
            })
          : undefined
      }
    >
      <div className="flex flex-wrap items-end gap-2">
        <PeriodField from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </div>

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={error instanceof Error ? error.message : "That report could not be loaded."}
        />
      ) : null}
      {built && built.difference !== 0 ? (
        <Alert
          role="alert"
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={`The movements differ from the change in cash by ${formatAmount(rupees(built.difference))}.`}
        />
      ) : null}

      <StatementTable
        caption="Cash flow"
        columns={["", "Amount"]}
        rows={built?.rows ?? []}
        loading={isPending}
      />
    </ReportFrame>
  );
}
